import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Upload, Camera, X } from 'lucide-react';
import { cn } from '../../lib/utils';

export default function FloatingActionButton() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const cameraRef = useRef(null);

  // Hide FAB on upload page (already there) and landing/login
  if (['/upload', '/login', '/register', '/'].includes(pathname)) return null;

  const handleUpload = () => {
    setOpen(false);
    navigate('/upload');
  };

  const handlePhoto = () => {
    setOpen(false);
    cameraRef.current?.click();
  };

  const onCameraCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      sessionStorage.setItem('fab_photo', JSON.stringify({ name: file.name, type: file.type, data: reader.result }));
      navigate('/upload?photo=true');
    };
    reader.readAsDataURL(file);
  };

  return (
    <>
      {/* Hidden camera input */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onCameraCapture}
      />

      {/* Backdrop — z-30, covers screen */}
      {open && (
        <div
          className="md:hidden fixed inset-0 bg-black/20 z-30"
          onClick={() => setOpen(false)}
          data-testid="fab-backdrop"
        />
      )}

      {/* Action items — z-40, above backdrop */}
      <div
        className={cn(
          'md:hidden fixed left-4 bottom-36 flex flex-col items-start gap-3 transition-all duration-200 z-40',
          open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
        )}
      >
        <button
          onClick={handleUpload}
          className="flex items-center gap-2 bg-white shadow-lg rounded-full pr-4 pl-3 py-2.5 text-sm font-medium text-zinc-800 active:scale-95 transition-transform"
          data-testid="fab-upload"
        >
          <div className="w-9 h-9 rounded-full bg-[#D94E18] flex items-center justify-center">
            <Upload className="w-4 h-4 text-white" />
          </div>
          Upload statement
        </button>

        <button
          onClick={handlePhoto}
          className="flex items-center gap-2 bg-white shadow-lg rounded-full pr-4 pl-3 py-2.5 text-sm font-medium text-zinc-800 active:scale-95 transition-transform"
          data-testid="fab-photo"
        >
          <div className="w-9 h-9 rounded-full bg-amber-500 flex items-center justify-center">
            <Camera className="w-4 h-4 text-white" />
          </div>
          Snap receipt
        </button>
      </div>

      {/* Main FAB button — z-40 */}
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'md:hidden fixed left-4 bottom-20 z-40 w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-200 active:scale-90',
          open ? 'bg-zinc-800 rotate-45' : 'bg-[#D94E18]'
        )}
        data-testid="fab-main"
      >
        {open ? <X className="w-6 h-6 text-white" /> : <Plus className="w-6 h-6 text-white" />}
      </button>
    </>
  );
}
